import { mixins, Modal, styled, SvgIcon } from '@bob/library'
import { usePath } from '@websrc/App/Hooks/usePath'
import { useBasketStateOptional } from '@websrc/Basket/useBasketState'
import { CSTopbar } from '@websrc/Typings/ContentStack'
import React, { ComponentPropsWithoutRef, useEffect, useRef, useState } from 'react'

import { CartModal } from './CartModal'

interface StyledButtonProps {
  quan: number
}

const StyledButton = styled.button<StyledButtonProps>`
  ${mixins.cta.tertiary('blue')}

  &:after {
    content: ${({ quan }: StyledButtonProps) => `'${quan}'`};
    position: absolute;
    height: 20px;
    width: 20px;
    line-height: 16px;
    font-size: 10px;
    border-radius: 50%;
    top: 2px;
    right: 2px;
    background: ${({ theme }: { theme: any }) => theme.background.yellow};
    display: ${({ quan }: StyledButtonProps) => (quan > 0 ? 'flex' : 'none')};
    justify-content: center;
    align-items: center;
  }

  & > svg {
    &:not(.svg-icon) {
      fill: initial;
    }

    &.svg-icon > path {
      &[stroke] {
        stroke: initial;
      }

      &[fill] {
        fill: initial;
      }
    }
  }
`

export interface CartButtonProps extends ComponentPropsWithoutRef<'button'> {
  shoppingCart: CSTopbar['shopping_cart']
}

export const CartButton = ({ shoppingCart, ...buttonProps }: CartButtonProps) => {
  const path = usePath()
  const [isOpen, setIsOpen] = useState(false)
  const basketState = useBasketStateOptional()
  const cartModelRef = useRef(basketState)

  useEffect(() => {
    if (!cartModelRef.current) {
      cartModelRef.current = basketState
    }

    if (
      !isOpen &&
      cartModelRef.current &&
      cartModelRef.current !== basketState &&
      // if the basket is empty, don't show
      Boolean(basketState?.items.length)
    ) {
      if (!path.slice('/en-us'.length).startsWith('/checkout')) {
        setIsOpen(true)
      }
    }

    cartModelRef.current = basketState
  }, [basketState, isOpen, cartModelRef, path])

  if (!shoppingCart) return null

  // Add the quantity of all
  const totalQuantity = basketState?.items.reduce((count, item) => count + item.quantity, 0) ?? 0

  return (
    <>
      <StyledButton {...buttonProps} onClick={() => setIsOpen(true)} quan={totalQuantity}>
        <SvgIcon name={'CART'} size={24} />
      </StyledButton>
      {isOpen && (
        <Modal closeCallback={() => setIsOpen(false)} position={'right'}>
          <CartModal onClose={() => setIsOpen(false)} />
        </Modal>
      )}
    </>
  )
}
